import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../Sidebar';
import 'bootstrap/dist/css/bootstrap.min.css';
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

function UserRegisterPage() {
  // Estados básicos
  const [nombres, setNombres] = useState('');
  const [correo, setCorreo] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('CC');
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastTipo, setToastTipo] = useState('success');
  
  // ⭐ NUEVO: Estados para roles dinámicos
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [cargandoRoles, setCargandoRoles] = useState(true);

  // Estados para firma
  const [firmaPreview, setFirmaPreview] = useState(null);
  const fileInputRef = useRef(null);

  // =====================================================
  // CARGAR ROLES AL INICIAR
  // =====================================================
  useEffect(() => {
    cargarRoles();
  }, []);

  const cargarRoles = async () => {
    try {
      setCargandoRoles(true);
      const correoUsuario = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/roles`, {
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Filtrar solo roles activos y ordenar por orden
        const rolesActivos = data.data
          .filter(r => r.activo !== false)
          .sort((a, b) => (a.orden || 0) - (b.orden || 0));
        
        setRoles(rolesActivos);
        
        // Seleccionar rol predeterminado si existe
        const rolPredeterminado = rolesActivos.find(r => r.esPredeterminado);
        if (rolPredeterminado) {
          setRoleId(rolPredeterminado._id);
          setRolSeleccionado(rolPredeterminado);
        } else if (rolesActivos.length > 0) {
          // Si no hay predeterminado, seleccionar el primero que no sea superadmin
          const primerRol = rolesActivos.find(r => r.codigo !== 'superadmin') || rolesActivos[0];
          setRoleId(primerRol._id);
          setRolSeleccionado(primerRol);
        }
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
      mostrarToast('Error al cargar roles del sistema', 'error');
    } finally {
      setCargandoRoles(false);
    }
  };

  // =====================================================
  // HANDLERS
  // =====================================================
  
  const handleRolChange = (e) => {
    const selectedId = e.target.value;
    setRoleId(selectedId);
    
    const rol = roles.find(r => r._id === selectedId);
    setRolSeleccionado(rol);
  };

  // Mostrar toast
  const mostrarToast = (mensaje, tipo = 'success', duracion = 3000) => {
    setMensaje(mensaje);
    setToastTipo(tipo);
    setShowToast(true);
    
    if (tipo !== 'loading') {
      setTimeout(() => {
        setShowToast(false);
      }, duracion);
    }
  };

  const ocultarToast = () => {
    setShowToast(false);
  };

  // Manejar archivo de firma
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      mostrarToast('Por favor selecciona un archivo de imagen', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      mostrarToast('La imagen no debe superar 2MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFirmaPreview(reader.result);
      mostrarToast('Firma cargada correctamente', 'success', 2000);
    };
    reader.readAsDataURL(file);
  };

  const handleEliminarFirma = () => {
    setFirmaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    mostrarToast('Firma eliminada', 'success', 2000);
  };

  // =====================================================
  // FUNCIÓN DE REGISTRO
  // =====================================================
  const handleRegister = async (e) => {
    e.preventDefault();

    // Validaciones
    const dominioPermitido = '@colombianadetrasplantes.com';
    if (!correo.endsWith(dominioPermitido)) {
      mostrarToast(`Solo se permiten correos del dominio ${dominioPermitido}`, 'error');
      return;
    }

    if (!numeroDocumento.trim()) {
      mostrarToast('El número de documento es obligatorio', 'error');
      return;
    }

    if (numeroDocumento.length < 5 || numeroDocumento.length > 15) {
      mostrarToast('El número de documento debe tener entre 5 y 15 caracteres', 'error');
      return;
    }

    if (!roleId) {
      mostrarToast('Debes seleccionar un rol', 'error');
      return;
    }

    mostrarToast('Creando usuario...', 'loading');

    try {
      const correoUsuario = localStorage.getItem('correo');
      
      // ⭐ NUEVO: Enviamos roleId en lugar de rol y permisos manuales
      const userData = { 
        nombres, 
        correo: correo.toLowerCase(),
        tipoDocumento,
        numeroDocumento: numeroDocumento.trim(),
        roleId: roleId,  // ⭐ ID del rol seleccionado
        rol: rolSeleccionado?.codigo || 'administrativo' // Mantener compatibilidad
      };

      if (firmaPreview) {
        userData.firma = firmaPreview;
      }

      const response = await fetch(`${API_BASE}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correoUsuario || ''
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        mostrarToast(`Usuario ${nombres} creado exitosamente con rol "${rolSeleccionado?.nombre}"`, 'success', 4000);
        
        // Limpiar formulario
        setNombres('');
        setCorreo('');
        setTipoDocumento('CC');
        setNumeroDocumento('');
        setFirmaPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Volver al rol predeterminado
        const rolPredeterminado = roles.find(r => r.esPredeterminado);
        if (rolPredeterminado) {
          setRoleId(rolPredeterminado._id);
          setRolSeleccionado(rolPredeterminado);
        }
      } else {
        mostrarToast(data.message || 'Error al crear usuario', 'error');
      }

    } catch (error) {
      console.error('Error al registrar usuario:', error);
      mostrarToast('Error al crear usuario. Verifica los datos.', 'error');
    }
  };

  // =====================================================
  // OBTENER PERMISOS ACTIVOS DEL ROL
  // =====================================================
  const getPermisosActivos = () => {
    if (!rolSeleccionado?.permisos) return [];
    
    return Object.entries(rolSeleccionado.permisos)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);
  };

  // Nombres legibles de permisos
  const nombresPermisos = {
    verDocumentosMedicos: 'Ver Documentos Médicos',
    verDocumentosAdministrativos: 'Ver Documentos Administrativos',
    enviarDocumentosMedicos: 'Enviar Documentos Médicos',
    enviarDocumentosAdministrativos: 'Enviar Documentos Administrativos',
    verReportes: 'Ver Reportes',
    firmarDocumentos: 'Firmar Documentos',
    anularDocumentos: 'Anular Documentos',
    gestionarUsuarios: 'Gestionar Usuarios',
    gestionarPlantillas: 'Gestionar Plantillas',
    configurarSistema: 'Configurar Sistema'
  };

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <>
      <style>{`
        .section-header {
          background: linear-gradient(135deg, #1a1e4b 0%, #2c3e50 100%);
          color: white;
          padding: 15px 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          text-align: center;
        }

        .info-card {
          background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
          border: 1px solid #bbdefb;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .rol-card {
          border: 2px solid #dee2e6;
          border-radius: 12px;
          padding: 15px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 10px;
        }

        .rol-card:hover {
          border-color: #007bff;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
        }

        .rol-card.selected {
          border-color: #007bff;
          background: #f0f7ff;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
        }

        .rol-color-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 8px;
        }

        .permiso-badge {
          display: inline-block;
          padding: 4px 10px;
          margin: 3px;
          border-radius: 20px;
          font-size: 12px;
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        }

        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 350px;
        }

        .toast-notification {
          background: white;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          padding: 20px;
          margin-bottom: 10px;
          border-left: 5px solid;
          animation: slideInRight 0.3s ease-out;
          position: relative;
        }

        .toast-notification.success { border-left-color: #28a745; }
        .toast-notification.error { border-left-color: #dc3545; }
        .toast-notification.loading { border-left-color: #007bff; }

        .toast-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          font-weight: bold;
        }

        .toast-icon {
          font-size: 20px;
          margin-right: 10px;
        }

        .toast-icon.success { color: #28a745; }
        .toast-icon.error { color: #dc3545; }
        .toast-icon.loading { color: #007bff; animation: spin 1s linear infinite; }

        .toast-close {
          position: absolute;
          top: 10px;
          right: 15px;
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #999;
        }

        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div className="d-flex">
        <Sidebar />

        <div className="flex-grow-1 p-4" style={{ marginLeft: '220px', transition: 'margin-left 0.3s' }}>
          <div className="container">
            <h1 className="text-center mb-4">
              <i className="bi bi-person-plus-fill me-2"></i>
              Registrar Usuario
            </h1>

            <form onSubmit={handleRegister} className="bg-white p-4 rounded shadow-sm">

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* INFORMACIÓN BÁSICA */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="section-header">
                <h5 className="mb-0">📋 Información Básica</h5>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Nombres completos:</label>
                  <input
                    type="text"
                    value={nombres}
                    onChange={(e) => setNombres(e.target.value)}
                    required
                    className="form-control"
                    placeholder="Ej: Juan Pérez González"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Correo electrónico:</label>
                  <input
                    type="email"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    required
                    className="form-control"
                    placeholder="usuario@colombianadetrasplantes.com"
                  />
                  <small className="text-muted">
                    Solo correos del dominio @colombianadetrasplantes.com
                  </small>
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-bold">Tipo de Documento:</label>
                  <select
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    required
                    className="form-select"
                  >
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="TI">Tarjeta de Identidad</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="PEP">Permiso Especial de Permanencia</option>
                  </select>
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-bold">Número de Documento:</label>
                  <input
                    type="text"
                    value={numeroDocumento}
                    onChange={(e) => setNumeroDocumento(e.target.value.replace(/[^0-9]/g, ''))}
                    required
                    className="form-control"
                    placeholder="Ej: 1234567890"
                    maxLength="15"
                  />
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* SELECCIÓN DE ROL */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="section-header">
                <h5 className="mb-0">👤 Rol del Usuario</h5>
              </div>

              {cargandoRoles ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="mt-2 text-muted">Cargando roles...</p>
                </div>
              ) : (
                <>
                  <div className="row mb-3">
                    {roles.map((rol) => (
                      <div className="col-md-4" key={rol._id}>
                        <div 
                          className={`rol-card ${roleId === rol._id ? 'selected' : ''}`}
                          onClick={() => {
                            setRoleId(rol._id);
                            setRolSeleccionado(rol);
                          }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            <span 
                              className="rol-color-indicator"
                              style={{ backgroundColor: rol.color || '#6b7280' }}
                            ></span>
                            <strong>{rol.nombre}</strong>
                            {rol.esInmutable && (
                              <span className="badge bg-warning text-dark ms-2">🔒</span>
                            )}
                          </div>
                          <small className="text-muted d-block">
                            {rol.descripcion || 'Sin descripción'}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mostrar permisos del rol seleccionado */}
                  {rolSeleccionado && (
                    <div className="info-card">
                      <h6 className="mb-2">
                        <i className="bi bi-shield-check me-2"></i>
                        Permisos del rol "{rolSeleccionado.nombre}":
                      </h6>
                      <div>
                        {getPermisosActivos().length > 0 ? (
                          getPermisosActivos().map(permiso => (
                            <span key={permiso} className="permiso-badge">
                              ✓ {nombresPermisos[permiso] || permiso}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted">Sin permisos especiales</span>
                        )}
                      </div>
                      <small className="text-muted d-block mt-2">
                        💡 Los permisos se heredan del rol. Puedes personalizarlos después desde "Administración de Usuarios".
                      </small>
                    </div>
                  )}
                </>
              )}

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* FIRMA (OPCIONAL) */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card border-0" style={{background: '#f8f9fa'}}>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <h6 className="mb-1 fw-bold">✍️ Firma del Usuario (Opcional)</h6>
                          <small className="text-muted">
                            Puedes agregarla después desde la gestión de usuarios
                          </small>
                        </div>
                        {firmaPreview && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleEliminarFirma}
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </div>

                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                          <small className="text-muted">JPG, PNG, GIF (máx. 2MB)</small>
                        </div>
                        <div className="col-md-4 text-center mt-3 mt-md-0">
                          {firmaPreview ? (
                            <img 
                              src={firmaPreview} 
                              alt="Firma" 
                              style={{
                                maxWidth: '100%',
                                maxHeight: '80px',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '5px',
                                background: 'white'
                              }}
                            />
                          ) : (
                            <div style={{
                              padding: '20px',
                              border: '1px dashed #dee2e6',
                              borderRadius: '4px',
                              background: 'white'
                            }}>
                              <small className="text-muted">Sin firma</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* BOTÓN ENVIAR */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="mt-4">
                <button
                  type="submit"
                  className="btn w-100 fw-bold py-3"
                  disabled={cargandoRoles || !roleId}
                  style={{ 
                    background: 'linear-gradient(135deg, #1a1e4b 0%, #2c3e50 100%)', 
                    color: 'white',
                    border: 'none',
                    fontSize: '16px'
                  }}
                >
                  {cargandoRoles ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Cargando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-plus me-2"></i>
                      Registrar Usuario
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* TOAST */}
      {showToast && (
        <div className="toast-container">
          <div className={`toast-notification ${toastTipo}`}>
            <button className="toast-close" onClick={ocultarToast}>×</button>
            <div className="toast-header">
              <div className={`toast-icon ${toastTipo}`}>
                {toastTipo === 'success' && '✓'}
                {toastTipo === 'error' && '✕'}
                {toastTipo === 'loading' && '⟳'}
              </div>
              <span>
                {toastTipo === 'success' && 'Éxito'}
                {toastTipo === 'error' && 'Error'}
                {toastTipo === 'loading' && 'Procesando'}
              </span>
            </div>
            <div className="toast-message">{mensaje}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserRegisterPage;